import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { HIDE_DECIMAL } from "@/hooks/useConfig";
import { TooltipComponentOption } from "echarts";
import { orderBy } from "lodash";
import { Theme } from "@mui/material";

export const getCustomMarker = (seriesName: string, color: string) => {
  const isDashed = seriesName !== "Actual Revenue" && seriesName !== "Actual Spend";
  if (isDashed) {
    return `<span style="display: inline-block; width: 14px; height: 2px; background: linear-gradient(to right, ${color} 40%, transparent 40%, transparent 60%, ${color} 60%); background-size: 4px 100%; margin-right: 6px; vertical-align: middle;"></span>`;
  }
  return `<span style="display: inline-block; width: 14px; height: 2px; background-color: ${color}; margin-right: 6px; vertical-align: middle;"></span>`;
};

export const getTimeSeriesTooltip = (
  theme: Theme,
  formatCurrency: FormatCurrencyFunction,
  refreshDateIndices: number[]
): TooltipComponentOption => {
  return {
    trigger: "axis",
    formatter: (params: any) => {
      const actualRevenue = params.find((param: any) => param.seriesName === "Actual Revenue");
      const actualSpend = params.find((param: any) => param.seriesName === "Actual Spend");

      const revisedSpend = params.find(
        (param: any) => param.seriesName === "Revised Spend Projection"
      );
      const baselineSpend = params.find(
        (param: any) => param.seriesName === "Baseline Spend Projection"
      );

      // Multiple series share the same name - filter to get all, then use index
      // Order: [0] = main line, [1] = lower bound, [2] = confidence interval
      const baselineRevenueSeries = params.filter(
        (param: any) => param.seriesName === "Baseline Revenue Forecast"
      );

      const [baselineRevenue, baselineLowerBound, baselineConfidenceInterval] =
        baselineRevenueSeries ?? [];

      const revisedRevenueSeries = params.filter(
        (param: any) => param.seriesName === "Revised Revenue Forecast"
      );

      const [revisedRevenue, revenueLowerBound, revenueConfidenceInterval] =
        revisedRevenueSeries ?? [];

      /**
       * Calculation of the baseline / revenue trajectories. We want this to exist if the necessary data is present,
       * AND if there is a gap between the lower and upper bound.
       */

      const baselineTrajectory = (() => {
        if (baselineConfidenceInterval?.value == null || baselineLowerBound?.value == null) {
          return undefined;
        }

        const lowerBound = baselineLowerBound.value;
        const intervalWidth = baselineConfidenceInterval.value;

        if (intervalWidth === 0) {
          return undefined;
        }

        return {
          ...baselineConfidenceInterval,
          seriesName: "Baseline Trajectory",
          color: theme.palette.mode === "dark" ? "#03BD4E" : "#09A856",
          value: baselineRevenue?.value,
          lowerBound: lowerBound,
          upperBound: lowerBound + intervalWidth,
        };
      })();

      const revenueTrajectory = (() => {
        if (revenueConfidenceInterval?.value == null || revenueLowerBound?.value == null) {
          return undefined;
        }

        const lowerBound = revenueLowerBound.value;
        const intervalWidth = revenueConfidenceInterval.value;

        if (intervalWidth === 0) {
          return undefined;
        }

        return {
          ...revenueConfidenceInterval,
          seriesName: "Revenue Trajectory",
          color: "#EC0089",
          value: revisedRevenue?.value,
          lowerBound: lowerBound,
          upperBound: lowerBound + intervalWidth,
        };
      })();

      const series = orderBy(
        [
          // Favor the baseline trajectory over the baseline revenue forecast, if it exists
          (baselineTrajectory || baselineRevenue) ?? null,
          baselineSpend,
          revisedSpend,
          actualSpend,
          // Favor the revenue trajectory over the revised revenue forecast, if it exists
          (revenueTrajectory || revisedRevenue) ?? null,
          actualRevenue,
        ].filter((series) => !!series && series.value !== null && series.value !== undefined),
        ["value"],
        ["desc"]
      );

      if (!series || series.length === 0) return "";

      const getRefreshDateText = (dataIndex: number) => {
        if (refreshDateIndices.includes(dataIndex)) {
          return `<div style="font-size: 12px;">(Data Refreshed)</div>`;
        }
        return "";
      };

      const getTrajectoryRangeText = (series: any) => {
        if (series.seriesName.includes("Trajectory")) {
          return `
            <div style='font-size: 12px; font-style: italic;'>
              (${formatCurrency(series?.lowerBound, HIDE_DECIMAL)} - ${formatCurrency(series?.upperBound, HIDE_DECIMAL)})
            </div>
          `;
        }
        return "";
      };

      return `
        <div>
          <div style="margin-bottom: 4px;">
            <span style="font-weight: 600; font-size: 16px;">${series[0].name}</span>
            ${getRefreshDateText(series[0].dataIndex)}
          </div>
          ${series
            .map(
              (series) => `
                <div style="margin-bottom: 8px;">
                  <div style="font-weight: 600;">
                    ${getCustomMarker(series.seriesName, series.color)}
                    ${series.seriesName}
                  </div>
                  <div style="margin-left: 24px">
                    <div>${formatCurrency(series.value, HIDE_DECIMAL)}</div>
                    ${getTrajectoryRangeText(series)}
                  </div>
                </div>
              `
            )
            .join("")}
        </div>
      `;
    },
  };
};
